/**
 * User Service
 * Handles all user-related operations
 */

import { Injectable, signal } from '@angular/core';
import { IUser, IUserProfile, UserRole, IUserPreferences } from '../models/models-user';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private readonly userSignal = signal<IUser>({
    id: '1',
    name: 'Admin User',
    email: 'admin@example.com',
    role: UserRole.ADMIN,
    createdAt: new Date(),
    lastLogin: new Date()
  });

  private readonly preferencesSignal = signal<IUserPreferences>({
    theme: 'light',
    language: 'es',
    notifications: true,
    emailNotifications: false
  });

  readonly user = this.userSignal.asReadonly();
  readonly preferences = this.preferencesSignal.asReadonly();

  /**
   * Get user initial for avatar
   */
  getUserInitial(): string {
    return this.userSignal().name.charAt(0).toUpperCase();
  }

  /**
   * Get user full name
   */
  getUserName(): string {
    return this.userSignal().name;
  }

  /**
   * Update user profile
   */
  updateUser(updates: Partial<IUser>): void {
    this.userSignal.update(current => ({ ...current, ...updates }));
  }

  /**
   * Update user preferences
   */
  updatePreferences(preferences: Partial<IUserPreferences>): void {
    this.preferencesSignal.update(current => ({
      ...current,
      ...preferences
    }));
  }

  /**
   * Get user profile
   */
  getUserProfile(): IUserProfile {
    const user = this.userSignal();
    const preferences = this.preferencesSignal();

    return {
      ...user,
      preferences
    };
  }

  /**
   * Logout user
   */
  logout(): void {
    this.userSignal.update(current => ({
      ...current,
      lastLogin: undefined
    }));
  }

  /**
   * Check if user is admin
   */
  isAdmin(): boolean {
    return this.userSignal().role === UserRole.ADMIN;
  }

  /**
   * Check if user has role
   */
  hasRole(role: UserRole): boolean {
    return this.userSignal().role === role;
  }
}
